// src/cockpit4/components/HealthScoreDashboard.tsx
// ============================================
// 企业健康度评分（P1-功能8）- v2.0 详细改进计划
//
// 设计：综合评分体系，0-100 分
//   1. 大数字显示综合健康度总分
//   2. 五维度分项得分进度条：
//      - 财务健康（25% 权重）
//      - 客户健康（25% 权重）
//      - 运营健康（20% 权重）
//      - 创新健康（15% 权重）
//      - 团队健康（15% 权重）
//   3. 趋势图（最近 30 天，用 recharts area chart）
//   4. 改进建议列表（基于低分维度自动生成建议）
//   5. 评分每日自动更新（每日首次访问时计算并存储到 localStorage）
//
// 评分算法：从 useDashboardData 获取各模块数据，按维度计算分项得分，加权计算总分
// 数据来源：useDashboardData 获取真实数据 + localStorage 历史记录（key: xeaos_health_history）
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Activity,
  Heart,
  DollarSign,
  Users,
  Settings,
  Lightbulb,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Lightbulb as SuggestionIcon,
} from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";

// ============================================
// 类型定义
// ============================================

/** 健康维度 ID */
type HealthDimensionId = "finance" | "customer" | "operations" | "innovation" | "team";

/** 健康维度评分 */
interface HealthDimension {
  id: HealthDimensionId;
  /** 中文名 */
  label: string;
  /** 英文名 */
  labelEn: string;
  /** 权重 0-1 */
  weight: number;
  /** 评分 0-100 */
  score: number;
  /** 主题色 */
  color: string;
  /** 图标 */
  icon: typeof DollarSign;
  /** 评分依据 */
  indicators: { name: string; value: string; status: "good" | "warning" | "bad" }[];
}

/** 历史趋势数据点 */
interface HealthHistoryPoint {
  /** YYYY-MM-DD */
  date: string;
  /** 综合分 */
  total: number;
  /** 各维度分 */
  finance: number;
  customer: number;
  operations: number;
  innovation: number;
  team: number;
}

/** 改进建议项 */
interface Suggestion {
  /** 维度 */
  dimension: HealthDimensionId;
  /** 建议内容 */
  text: string;
  /** 优先级 */
  priority: "high" | "medium" | "low";
  /** 当前得分 */
  currentScore: number;
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_health_history";
const LAST_CALC_KEY = "xeaos_health_last_calc_date";

const DIMENSION_CONFIG: Record<
  HealthDimensionId,
  { label: string; labelEn: string; weight: number; color: string; icon: typeof DollarSign }
> = {
  finance: { label: "财务健康", labelEn: "Finance", weight: 0.25, color: "#10B981", icon: DollarSign },
  customer: { label: "客户健康", labelEn: "Customer", weight: 0.25, color: "#3B82F6", icon: Users },
  operations: { label: "运营健康", labelEn: "Operations", weight: 0.20, color: "#EC4899", icon: Settings },
  innovation: { label: "创新健康", labelEn: "Innovation", weight: 0.15, color: "#F59E0B", icon: Lightbulb },
  team: { label: "团队健康", labelEn: "Team", weight: 0.15, color: "#A855F7", icon: Activity },
};

// ============================================
// 评分算法：基于真实数据计算各维度得分
// ============================================

interface ScoreContext {
  /** CEO Dashboard 数据 */
  ceoData: ReturnType<typeof useDashboardData>["ceo"];
  /** CRM 数据 */
  crmData: ReturnType<typeof useDashboardData>["crm"];
  /** Marketing 数据 */
  marketingData: ReturnType<typeof useDashboardData>["marketing"];
  /** Hermes 数据 */
  hermesData: ReturnType<typeof useDashboardData>["hermes"];
  /** 项目数据 */
  projectsData: ReturnType<typeof useDashboardData>["projects"];
}

function computeFinanceScore(ctx: ScoreContext): HealthDimension {
  const kpi = ctx.ceoData.kpi;
  const indicators: HealthDimension["indicators"] = [];
  let score = 0;

  // 收入指标（40 分）
  const revenueScore = kpi.revenue >= 1000 ? 40 : kpi.revenue > 0 ? 25 : 0;
  score += revenueScore;
  indicators.push({
    name: "今日收入",
    value: `¥${kpi.revenue.toLocaleString()}`,
    status: kpi.revenue >= 1000 ? "good" : kpi.revenue > 0 ? "warning" : "bad",
  });

  // 利润指标（30 分）
  const profitScore = kpi.profit > 0 ? 30 : kpi.profit === 0 ? 15 : 5;
  score += profitScore;
  indicators.push({
    name: "今日利润",
    value: `¥${kpi.profit.toLocaleString()}`,
    status: kpi.profit > 0 ? "good" : kpi.profit === 0 ? "warning" : "bad",
  });

  // 现金流（30 分）
  const cashScore = kpi.cash > 0 ? 30 : kpi.cash === 0 ? 15 : 0;
  score += cashScore;
  indicators.push({
    name: "现金流",
    value: `¥${kpi.cash.toLocaleString()}`,
    status: kpi.cash > 0 ? "good" : kpi.cash === 0 ? "warning" : "bad",
  });

  // 数据降级时扣分
  if (ctx.ceoData.degraded) score = Math.max(0, score - 20);

  return {
    id: "finance",
    label: DIMENSION_CONFIG.finance.label,
    labelEn: DIMENSION_CONFIG.finance.labelEn,
    weight: DIMENSION_CONFIG.finance.weight,
    score: Math.min(100, score),
    color: DIMENSION_CONFIG.finance.color,
    icon: DIMENSION_CONFIG.finance.icon,
    indicators,
  };
}

function computeCustomerScore(ctx: ScoreContext): HealthDimension {
  const kpi = ctx.ceoData.kpi;
  const stats = ctx.crmData.stats;
  const indicators: HealthDimension["indicators"] = [];
  let score = 0;

  // 新增客户（35 分）
  const newCustScore = kpi.newCustomers > 0 ? 35 : ctx.ceoData.degraded ? 10 : 0;
  score += newCustScore;
  indicators.push({
    name: "今日新增客户",
    value: `${kpi.newCustomers} 人`,
    status: kpi.newCustomers > 0 ? "good" : "bad",
  });

  // 转化率（30 分）
  const convScore = kpi.conversionRate >= 30 ? 30 : kpi.conversionRate >= 10 ? 20 : 10;
  score += convScore;
  indicators.push({
    name: "转化率",
    value: `${kpi.conversionRate}%`,
    status: kpi.conversionRate >= 30 ? "good" : kpi.conversionRate >= 10 ? "warning" : "bad",
  });

  // 客户留存（基于 CRM 总数，35 分）
  const totalCustomers = stats?.total || 0;
  const retentionScore = totalCustomers >= 50 ? 35 : totalCustomers >= 10 ? 25 : totalCustomers > 0 ? 15 : 0;
  score += retentionScore;
  indicators.push({
    name: "客户总数",
    value: `${totalCustomers}`,
    status: totalCustomers >= 50 ? "good" : totalCustomers > 0 ? "warning" : "bad",
  });

  // 数据降级扣分
  if (ctx.crmData.degraded) score = Math.max(0, score - 20);

  return {
    id: "customer",
    label: DIMENSION_CONFIG.customer.label,
    labelEn: DIMENSION_CONFIG.customer.labelEn,
    weight: DIMENSION_CONFIG.customer.weight,
    score: Math.min(100, score),
    color: DIMENSION_CONFIG.customer.color,
    icon: DIMENSION_CONFIG.customer.icon,
    indicators,
  };
}

function computeOperationsScore(ctx: ScoreContext): HealthDimension {
  const indicators: HealthDimension["indicators"] = [];
  let score = 0;

  // 任务完成情况（50 分）- 从 Hermes 完成任务派生
  const completedToday = ctx.hermesData.completedToday.length;
  const failedToday = ctx.hermesData.failed.length;
  const taskScore = completedToday >= 5 ? 50 : completedToday > 0 ? 30 : 10;
  score += taskScore;
  indicators.push({
    name: "今日完成任务",
    value: `${completedToday} 项`,
    status: completedToday >= 5 ? "good" : completedToday > 0 ? "warning" : "bad",
  });

  // 任务失败率（25 分）
  const failRate = completedToday + failedToday > 0 ? failedToday / (completedToday + failedToday) : 0;
  const failScore = failRate === 0 ? 25 : failRate < 0.2 ? 15 : 5;
  score += failScore;
  indicators.push({
    name: "任务失败数",
    value: `${failedToday} 项`,
    status: failedToday === 0 ? "good" : failedToday < 3 ? "warning" : "bad",
  });

  // Pipeline 商机（25 分）
  const pipelineCount = ctx.crmData.pipeline.reduce((s, p) => s + p.count, 0);
  const pipelineScore = pipelineCount >= 10 ? 25 : pipelineCount > 0 ? 15 : 5;
  score += pipelineScore;
  indicators.push({
    name: "Pipeline 商机",
    value: `${pipelineCount} 个`,
    status: pipelineCount >= 10 ? "good" : pipelineCount > 0 ? "warning" : "bad",
  });

  // 数据降级扣分
  if (ctx.hermesData.degraded) score = Math.max(0, score - 20);

  return {
    id: "operations",
    label: DIMENSION_CONFIG.operations.label,
    labelEn: DIMENSION_CONFIG.operations.labelEn,
    weight: DIMENSION_CONFIG.operations.weight,
    score: Math.min(100, score),
    color: DIMENSION_CONFIG.operations.color,
    icon: DIMENSION_CONFIG.operations.icon,
    indicators,
  };
}

function computeInnovationScore(ctx: ScoreContext): HealthDimension {
  const indicators: HealthDimension["indicators"] = [];
  let score = 0;

  // 内容产出（40 分）- 从 Marketing 派生
  const totalContent =
    ctx.marketingData.platforms.wechat_official.topContent.length +
    ctx.marketingData.platforms.xiaohongshu.topContent.length;
  const contentScore = totalContent >= 5 ? 40 : totalContent > 0 ? 25 : 5;
  score += contentScore;
  indicators.push({
    name: "内容产出",
    value: `${totalContent} 篇`,
    status: totalContent >= 5 ? "good" : totalContent > 0 ? "warning" : "bad",
  });

  // 内容 ROI（30 分）
  const contentRoi = ctx.ceoData.kpi.contentRoi;
  const roiScore = contentRoi >= 50 ? 30 : contentRoi >= 20 ? 20 : 10;
  score += roiScore;
  indicators.push({
    name: "内容 ROI",
    value: `${contentRoi}%`,
    status: contentRoi >= 50 ? "good" : contentRoi >= 20 ? "warning" : "bad",
  });

  // 活跃品牌（30 分）
  const activeBrands = ctx.ceoData.kpi.activeBrands;
  const brandScore = activeBrands >= 3 ? 30 : activeBrands > 0 ? 20 : 5;
  score += brandScore;
  indicators.push({
    name: "活跃品牌",
    value: `${activeBrands} 个`,
    status: activeBrands >= 3 ? "good" : activeBrands > 0 ? "warning" : "bad",
  });

  return {
    id: "innovation",
    label: DIMENSION_CONFIG.innovation.label,
    labelEn: DIMENSION_CONFIG.innovation.labelEn,
    weight: DIMENSION_CONFIG.innovation.weight,
    score: Math.min(100, score),
    color: DIMENSION_CONFIG.innovation.color,
    icon: DIMENSION_CONFIG.innovation.icon,
    indicators,
  };
}

function computeTeamScore(ctx: ScoreContext): HealthDimension {
  const indicators: HealthDimension["indicators"] = [];
  let score = 60; // 团队数据暂无直接来源，给基础分

  // Hermes 任务运行情况（间接反映团队协作效率）
  const runningCount = ctx.hermesData.running.length;
  indicators.push({
    name: "运行中任务",
    value: `${runningCount} 项`,
    status: runningCount > 0 ? "good" : "warning",
  });

  // 高优客户跟进（反映销售团队负荷）
  const priorityCustomers = ctx.crmData.priorityCustomers.length;
  indicators.push({
    name: "高优客户",
    value: `${priorityCustomers} 个`,
    status: priorityCustomers > 0 ? "good" : "warning",
  });

  // 任务失败率（反映团队执行质量）
  const completedToday = ctx.hermesData.completedToday.length;
  const failedToday = ctx.hermesData.failed.length;
  const failRate = completedToday + failedToday > 0 ? failedToday / (completedToday + failedToday) : 0;
  indicators.push({
    name: "任务成功率",
    value: `${Math.round((1 - failRate) * 100)}%`,
    status: failRate < 0.1 ? "good" : failRate < 0.3 ? "warning" : "bad",
  });

  // 调整分数
  if (failRate > 0.3) score -= 20;
  else if (failRate > 0.1) score -= 10;
  else if (completedToday > 0) score += 20;

  if (ctx.hermesData.degraded) score = Math.max(20, score - 15);

  return {
    id: "team",
    label: DIMENSION_CONFIG.team.label,
    labelEn: DIMENSION_CONFIG.team.labelEn,
    weight: DIMENSION_CONFIG.team.weight,
    score: Math.min(100, Math.max(0, score)),
    color: DIMENSION_CONFIG.team.color,
    icon: DIMENSION_CONFIG.team.icon,
    indicators,
  };
}

// ============================================
// 工具函数
// ============================================

/** 加载历史趋势数据（localStorage） */
function loadHistory(): HealthHistoryPoint[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as HealthHistoryPoint[];
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    // 忽略
  }
  return [];
}

/** 保存历史趋势数据 */
function saveHistory(history: HealthHistoryPoint[]) {
  try {
    // 保留最近 30 天
    const trimmed = history.slice(-30);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // 忽略
  }
}

/** 获取健康等级 */
function getHealthLevel(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "优秀", color: "#10B981" };
  if (score >= 60) return { label: "良好", color: "#3B82F6" };
  if (score >= 40) return { label: "关注", color: "#F59E0B" };
  return { label: "危险", color: "#EF4444" };
}

// ============================================
// 主组件
// ============================================

export default function HealthScoreDashboard() {
  const dashboard = useDashboardData();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<HealthHistoryPoint[]>([]);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  // ============================================
  // 计算五维度得分
  // ============================================
  const dimensions: HealthDimension[] = useMemo(() => {
    const ctx: ScoreContext = {
      ceoData: dashboard.ceo,
      crmData: dashboard.crm,
      marketingData: dashboard.marketing,
      hermesData: dashboard.hermes,
      projectsData: dashboard.projects,
    };
    return [
      computeFinanceScore(ctx),
      computeCustomerScore(ctx),
      computeOperationsScore(ctx),
      computeInnovationScore(ctx),
      computeTeamScore(ctx),
    ];
  }, [dashboard]);

  // 综合得分（加权平均）
  const totalScore = useMemo(() => {
    return Math.round(
      dimensions.reduce((sum, d) => sum + d.score * d.weight, 0)
    );
  }, [dimensions]);

  const healthLevel = getHealthLevel(totalScore);

  // ============================================
  // 生成改进建议（基于低分维度）
  // ============================================
  const suggestions: Suggestion[] = useMemo(() => {
    const result: Suggestion[] = [];
    dimensions.forEach((d) => {
      if (d.score < 60) {
        // 根据维度生成针对性建议
        let text = "";
        let priority: Suggestion["priority"] = "medium";
        if (d.id === "finance") {
          text = "财务得分偏低，建议加强收入变现能力，控制成本支出，关注现金流健康";
          priority = d.score < 40 ? "high" : "medium";
        } else if (d.id === "customer") {
          text = "客户健康度偏低，建议加强客户获取和留存，提升销售转化率";
          priority = d.score < 40 ? "high" : "medium";
        } else if (d.id === "operations") {
          text = "运营效率待提升，建议优化任务执行流程，降低任务失败率";
          priority = d.score < 40 ? "high" : "medium";
        } else if (d.id === "innovation") {
          text = "创新能力偏弱，建议加大内容产出，提升内容 ROI 和品牌活跃度";
          priority = "medium";
        } else if (d.id === "team") {
          text = "团队健康度待关注，建议优化团队负荷，提升任务成功率";
          priority = "medium";
        }
        result.push({ dimension: d.id, text, priority, currentScore: d.score });
      }
    });
    // 按优先级排序
    const priorityWeight = { high: 3, medium: 2, low: 1 };
    result.sort((a, b) => priorityWeight[b.priority] - priorityWeight[a.priority]);
    return result;
  }, [dimensions]);

  // ============================================
  // 每日自动更新：仅首次挂载时计算并存储一次
  // v6.3.24 修复渲染异常：原实现 useEffect 依赖 calculateAndStore（依赖整个 dashboard 对象，
  // 而 useDashboardData 每 5 秒刷新重建对象）→ 反复触发 setLoading(true)+setMessage → 界面闪烁抖动。
  // 现改为：ref 保存最新评分，effect 只执行一次；手动重算按钮始终用最新值。
  // ============================================
  const dimensionsRef = useRef(dimensions);
  dimensionsRef.current = dimensions;
  const totalScoreRef = useRef(totalScore);
  totalScoreRef.current = totalScore;

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        // 加载已有历史（只加载一次，不随刷新反复重读）
        const existingHistory = loadHistory();
        if (cancelled) return;
        setHistory(existingHistory);

        const today = new Date().toISOString().slice(0, 10);
        const lastCalcDate = localStorage.getItem(LAST_CALC_KEY);

        // 如果今天还没计算过，则计算并存储
        if (lastCalcDate !== today) {
          const dims = dimensionsRef.current;
          const newPoint: HealthHistoryPoint = {
            date: today,
            total: totalScoreRef.current,
            finance: dims[0].score,
            customer: dims[1].score,
            operations: dims[2].score,
            innovation: dims[3].score,
            team: dims[4].score,
          };
          // 移除今天已有的记录（避免重复）
          const filtered = existingHistory.filter((p) => p.date !== today);
          const newHistory = [...filtered, newPoint].sort((a, b) => a.date.localeCompare(b.date));
          saveHistory(newHistory);
          if (!cancelled) setHistory(newHistory);
          localStorage.setItem(LAST_CALC_KEY, today);
        }
      } catch {
        // 忽略：评分失败不影响页面展示
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 手动重算（覆盖今日记录） */
  const recalculate = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const dims = dimensionsRef.current;
      const newPoint: HealthHistoryPoint = {
        date: today,
        total: totalScoreRef.current,
        finance: dims[0].score,
        customer: dims[1].score,
        operations: dims[2].score,
        innovation: dims[3].score,
        team: dims[4].score,
      };
      const filtered = history.filter((p) => p.date !== today);
      const newHistory = [...filtered, newPoint].sort((a, b) => a.date.localeCompare(b.date));
      saveHistory(newHistory);
      setHistory(newHistory);
      localStorage.setItem(LAST_CALC_KEY, today);
      setMessage({ type: "ok", text: `✅ 已重新计算健康度评分（${today}）` });
    } catch (err) {
      setMessage({ type: "err", text: `❌ 重算失败：${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, [history]);

  // 趋势数据（最近 30 天）
  const trendData = useMemo(() => {
    return history.slice(-30).map((p) => ({
      date: p.date.slice(5), // MM-DD
      total: p.total,
      finance: p.finance,
      customer: p.customer,
      operations: p.operations,
      innovation: p.innovation,
      team: p.team,
    }));
  }, [history]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${healthLevel.color}80, transparent)` }}
      />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center"
            style={{ background: `${healthLevel.color}15`, border: `1px solid ${healthLevel.color}30` }}
          >
            <Heart size={13} style={{ color: healthLevel.color }} />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">企业健康度评分</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Health Score · 综合 {totalScore}/100（{healthLevel.label}）· {suggestions.length} 项改进建议
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={recalculate}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
            title="立即重算健康度评分"
          >
            {loading ? (
              <Loader2 size={11} className="text-emerald-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-emerald-400" />
            )}
            <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
              {loading ? "计算中..." : "立即重算"}
            </span>
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      {/* 主内容区：大数字 + 五维度进度条 + 趋势图 */}
      <div className="grid grid-cols-12 gap-3 p-4">
        {/* 左侧：综合得分大数字 */}
        <div className="col-span-4 rounded-lg border border-white/[0.04] bg-white/[0.02] p-4 flex flex-col items-center justify-center">
          <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-2">
            综合健康度
          </div>
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="text-[56px] font-bold leading-none"
            style={{ color: healthLevel.color, fontFamily: "monospace" }}
          >
            {totalScore}
          </motion.div>
          <div className="text-[11px] font-mono text-gray-500 mt-1">/ 100</div>
          <div
            className="mt-2 px-2 py-0.5 rounded text-[10px] font-mono uppercase"
            style={{ background: `${healthLevel.color}15`, color: healthLevel.color }}
          >
            {healthLevel.label}
          </div>
          {/* 趋势对比 */}
          {history.length >= 2 && (
            <div className="mt-3 flex items-center gap-2 text-[10px] font-mono">
              {(() => {
                const last = history[history.length - 1].total;
                const prev = history[history.length - 2].total;
                const diff = last - prev;
                if (diff > 0) {
                  return (
                    <span className="flex items-center gap-0.5 text-emerald-400">
                      <TrendingUp size={10} />
                      +{diff} 较昨日
                    </span>
                  );
                } else if (diff < 0) {
                  return (
                    <span className="flex items-center gap-0.5 text-red-400">
                      <TrendingDown size={10} />
                      {diff} 较昨日
                    </span>
                  );
                }
                return <span className="text-gray-500">— 较昨日持平</span>;
              })()}
            </div>
          )}
        </div>

        {/* 右侧：五维度进度条 */}
        <div className="col-span-8 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              五维度分项得分
            </span>
            <span className="text-[9px] font-mono text-gray-600">
              权重：财务25% · 客户25% · 运营20% · 创新15% · 团队15%
            </span>
          </div>
          {dimensions.map((d) => {
            const Icon = d.icon;
            const lvl = getHealthLevel(d.score);
            return (
              <div
                key={d.id}
                className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="w-5 h-5 rounded flex items-center justify-center"
                    style={{ background: `${d.color}15` }}
                  >
                    <Icon size={10} style={{ color: d.color }} />
                  </div>
                  <span className="text-[11px] text-gray-200 flex-1">{d.label}</span>
                  <span className="text-[9px] font-mono text-gray-600">
                    权重 {Math.round(d.weight * 100)}%
                  </span>
                  <span
                    className="text-[12px] font-mono font-bold w-8 text-right"
                    style={{ color: lvl.color }}
                  >
                    {d.score}
                  </span>
                  <span
                    className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                    style={{ background: `${lvl.color}15`, color: lvl.color }}
                  >
                    {lvl.label}
                  </span>
                </div>
                {/* 进度条 */}
                <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${d.score}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{ background: d.color }}
                  />
                </div>
                {/* 指标 */}
                <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                  {d.indicators.map((ind, i) => (
                    <span
                      key={i}
                      className="text-[9px] font-mono"
                      style={{
                        color:
                          ind.status === "good" ? "#10B981"
                          : ind.status === "warning" ? "#F59E0B"
                          : "#EF4444",
                      }}
                    >
                      {ind.name}：{ind.value}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 趋势图 */}
      <div className="px-4 pb-3">
        <div className="rounded-lg border border-white/[0.04] bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              最近 30 天趋势
            </span>
            <span className="text-[9px] font-mono text-gray-600">
              {trendData.length} 条历史记录
            </span>
          </div>
          {trendData.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-[11px] font-mono text-gray-600">
              暂无历史数据 · 首次访问后自动开始记录
            </div>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                  <defs>
                    <linearGradient id="healthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={healthLevel.color} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={healthLevel.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#6B7280", fontSize: 9, fontFamily: "monospace" }}
                    stroke="rgba(255,255,255,0.04)"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: "#6B7280", fontSize: 9, fontFamily: "monospace" }}
                    stroke="rgba(255,255,255,0.04)"
                  />
                  <Tooltip
                    contentStyle={{
                      background: "rgba(10,14,26,0.95)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      fontSize: "11px",
                      fontFamily: "monospace",
                    }}
                    labelStyle={{ color: "#E5E7EB" }}
                    itemStyle={{ color: "#9CA3AF" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="total"
                    name="综合健康度"
                    stroke={healthLevel.color}
                    fill="url(#healthGrad)"
                    strokeWidth={1.5}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* 改进建议 */}
      <div className="px-4 pb-4">
        <div className="rounded-lg border border-white/[0.04] bg-white/[0.02] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <SuggestionIcon size={11} className="text-amber-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                改进建议（基于低分维度自动生成）
              </span>
            </div>
            <span className="text-[9px] font-mono text-gray-600">
              {suggestions.length} 项建议
            </span>
          </div>
          {suggestions.length === 0 ? (
            <div className="py-4 text-center">
              <CheckCircle2 size={20} className="mx-auto text-emerald-400 mb-1" />
              <p className="text-[11px] font-mono text-gray-400">
                所有维度均达标，继续保持！
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {suggestions.map((s, idx) => {
                const dimCfg = DIMENSION_CONFIG[s.dimension];
                const priorityCfg = {
                  high: { color: "#EF4444", label: "高优先级" },
                  medium: { color: "#F59E0B", label: "中优先级" },
                  low: { color: "#3B82F6", label: "低优先级" },
                }[s.priority];
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-start gap-2 p-2 rounded-md border border-white/[0.06] bg-white/[0.02]"
                  >
                    <AlertTriangle
                      size={11}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: priorityCfg.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span
                          className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                          style={{ background: `${dimCfg.color}15`, color: dimCfg.color }}
                        >
                          {dimCfg.label}
                        </span>
                        <span
                          className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                          style={{ background: `${priorityCfg.color}15`, color: priorityCfg.color }}
                        >
                          {priorityCfg.label}
                        </span>
                        <span className="text-[9px] font-mono text-gray-600">
                          当前得分：{s.currentScore}/100
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-300 leading-snug">{s.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
